import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Grid,
  Stack,
} from '@mui/material';
import {
  AutoFixHigh,
  CheckCircle,
  OpenInNew,
} from '@mui/icons-material';
import type { Issue } from '../../../store/api/githubApi';
import { SEVERITY_COLOR, isIssueFixable } from '../constants';
import { CATEGORY_LABELS } from './IssueRow';
import { SideDrawer } from '../../../components/common/SideDrawer';

interface IssueDetailModalProps {
  open: boolean;
  onClose: () => void;
  issue: Issue | null;
  onFix?: (issueId: number) => void;
  isFixing?: boolean;
  scanId?: string;
}

export const IssueDetailModal: React.FC<IssueDetailModalProps> = ({
  open,
  onClose,
  issue,
  onFix,
  isFixing,
}) => {
  if (!issue) return null;

  const isFixable = isIssueFixable(issue);
  const isGenerating = issue.status === 'fix_generating' || isFixing;
  const isPrRaised = issue.status === 'pr_raised';

  // Extract Cve or OWASP if present
  const cveRef = issue.cve_id || (issue.cve_ids && issue.cve_ids[0]);

  // Determine suggested fix text from tool_detail or fallback guidance
  let suggestedFixText = '';
  if (issue.tool_detail && issue.tool_detail.includes('How to fix:')) {
    const parts = issue.tool_detail.split(' | ');
    const fixPart = parts.find((p) => p.startsWith('How to fix:'));
    if (fixPart) suggestedFixText = fixPart.replace('How to fix:', '').trim();
  }
  if (!suggestedFixText && isFixable) {
    suggestedFixText = `Apply automated patch targeting ${issue.file_path.split('/').pop() ?? 'the source file'} to address ${issue.title.toLowerCase()}.`;
  } else if (!suggestedFixText) {
    suggestedFixText = `Manual refactoring recommended: review ${issue.file_path} around line ${issue.line_start ?? 1} for ${issue.category} compliance.`;
  }

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      width={{ xs: '100vw', sm: '75vw', md: 620 }}
      maxWidth={{ xs: '100vw', sm: '80vw', md: '50vw' }}
      title={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={issue.severity}
              size="small"
              color={SEVERITY_COLOR[issue.severity] ?? 'default'}
              sx={{ textTransform: 'capitalize', fontWeight: 700 }}
            />
            <Chip
              label={issue.category}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'capitalize', fontSize: 11 }}
            />
            {cveRef && (
              <Chip
                label={cveRef}
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontSize: 11, fontWeight: 700 }}
              />
            )}
            {issue.ai_priority_score != null && (
              <Chip
                label={`Priority ${Math.round(issue.ai_priority_score)}`}
                size="small"
                sx={{ bgcolor: 'grey.100', fontWeight: 700, fontSize: 11 }}
              />
            )}
          </Box>
          <Typography variant="h6" fontWeight={800} lineHeight={1.3} sx={{ color: '#0f172a', fontSize: '1.15rem' }}>
            {issue.title}
          </Typography>
        </Box>
      }
      description={`${issue.file_path}${issue.line_start ? `:${issue.line_start}` : ''}`}
      footer={
        <>
          <Button
            onClick={onClose}
            sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
          >
            Close
          </Button>
          {isFixable && !isPrRaised && !isGenerating && onFix && (
            <Button
              onClick={() => onFix(issue.id)}
              variant="contained"
              color="primary"
              startIcon={<AutoFixHigh />}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: '10px',
                px: 2.5,
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
              }}
            >
              Generate Fix
            </Button>
          )}
        </>
      }
    >
      {/* Structured AI-Fix Workflow Steps */}
      <Stack spacing={2.5}>
        {/* STEP 1: Issue Context */}
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2, bgcolor: '#f8fafc' }}>
          <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            1. Issue Context
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">Detected By</Typography>
              <Typography variant="body2" fontWeight={600}>
                {CATEGORY_LABELS[issue.category] ?? 'KodeGauge Analysis'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">Fix Complexity</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                {issue.ai_fix_complexity ?? 'Standard'}
              </Typography>
            </Grid>
            {issue.ai_estimated_fix_time && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">Est. Fix Time</Typography>
                <Typography variant="body2" fontWeight={600}>{issue.ai_estimated_fix_time}</Typography>
              </Grid>
            )}
            {issue.security_risk_usd != null && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">Breach Risk</Typography>
                <Typography variant="body2" fontWeight={700} color="error.main">
                  ${issue.security_risk_usd.toLocaleString()}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* STEP 2: Why This Matters */}
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2 }}>
          <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            2. Impact & Guidance
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#334155', lineHeight: 1.6 }}>
            {issue.ai_explanation || issue.ai_priority_reasoning || issue.tool_detail || 'No extended description available for this finding.'}
          </Typography>
        </Box>

        {/* STEP 3: Automated Remediation Plan */}
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2, bgcolor: '#f8fafc' }}>
          <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            3. AI Recommended Action
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#1e293b', fontWeight: 600 }}>
            {suggestedFixText}
          </Typography>
        </Box>

        {/* Workflow Progress */}
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2 }}>
          <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
            Remediation Pipeline
          </Typography>
          <Grid container spacing={1.5}>
            {/* Step 4: AI Agent */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff', height: '100%' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
                  Stage 1: AI Agent
                </Typography>
                {isGenerating ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="primary.main" fontWeight={700}>Synthesizing...</Typography>
                  </Box>
                ) : isPrRaised ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <CheckCircle sx={{ fontSize: 15, color: 'success.main' }} />
                    <Typography variant="caption" color="success.main" fontWeight={700}>Generated</Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {isFixable ? 'Ready to trigger' : 'Manual fix required'}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Step 5: Review Changes */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff', height: '100%' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
                  Stage 2: Validation
                </Typography>
                {isPrRaised ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', lineHeight: 1.4 }}>
                    Patch verified against AST rules.
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                    Runs during generation.
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Step 6: Create PR / View PR */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff', height: '100%' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
                  Stage 3: Pull Request
                </Typography>
                {isPrRaised && issue.pr_url ? (
                  <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    startIcon={<OpenInNew />}
                    onClick={() => window.open(issue.pr_url!, '_blank')}
                    sx={{ mt: 1, textTransform: 'none', width: '100%', fontSize: 11, fontWeight: 700 }}
                  >
                    View PR #{issue.pr_number}
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                    PR created automatically.
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </SideDrawer>
  );
};

export default IssueDetailModal;
